import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  InputAdornment,
  Stack,
} from '@mui/material'
import {
  Add,
  Send,
  Build,
  CheckCircle,
  LocalShipping,
  FilterList,
  Clear,
} from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, PermissionGate, usePermissions, ExportButtons } from '@components/common'
import api from '@services/api'
import { toast } from 'react-toastify'
import { formatDate, formatDateTime } from '@utils/dateUtils'

const STATUS_COLORS = {
  scheduled: 'default',
  sent_out: 'warning',
  in_repair: 'info',
  ready_pickup: 'primary',
  returned: 'success',
  cancelled: 'error',
}

const REPAIR_TYPES = [
  { value: 'warranty', label: 'Warranty Repair' },
  { value: 'contract', label: 'Service Contract' },
  { value: 'paid', label: 'Paid Repair' },
  { value: 'calibration', label: 'External Calibration' },
  { value: 'upgrade', label: 'Upgrade/Modification' },
]

const columns = [
  { field: 'repair_number', headerName: 'Repair #', sortable: true, width: 130 },
  { field: 'asset_name', headerName: 'Asset', sortable: true },
  { field: 'asset_tag', headerName: 'Tag', sortable: true, width: 100 },
  { field: 'vendor_name', headerName: 'Vendor', sortable: true },
  {
    field: 'repair_type',
    headerName: 'Type',
    sortable: true,
    renderCell: (value) => value?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (value) => (
      <Chip
        label={value?.replace('_', ' ').toUpperCase()}
        color={STATUS_COLORS[value] || 'default'}
        size="small"
      />
    ),
  },
  {
    field: 'sent_date',
    headerName: 'Sent Date',
    sortable: true,
    renderCell: (value) => value ? formatDate(value) : '-',
  },
  {
    field: 'expected_return_date',
    headerName: 'Expected Return',
    sortable: true,
    renderCell: (value) => value ? formatDate(value) : '-',
  },
]

const initialFormData = {
  asset: '',
  vendor: '',
  problem_description: '',
  repair_type: 'paid',
  expected_return_date: '',
  estimated_cost: '',
  notes: '',
}

const ExternalRepairsList = () => {
  const { hasPermission } = usePermissions()
  const [repairs, setRepairs] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedRepair, setSelectedRepair] = useState(null)
  const [formData, setFormData] = useState(initialFormData)
  const [formLoading, setFormLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [showFilters, setShowFilters] = useState(false)

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    repair_type: '',
  })

  // Options for dropdowns
  const [assets, setAssets] = useState([])
  const [vendors, setVendors] = useState([])

  // Export columns
  const exportColumns = useMemo(() => [
    { field: 'repair_number', headerName: 'Repair #' },
    { field: 'asset_name', headerName: 'Asset' },
    { field: 'asset_tag', headerName: 'Tag' },
    { field: 'vendor_name', headerName: 'Vendor' },
    { field: 'repair_type', headerName: 'Type', renderValue: (v) => v?.replace('_', ' ') },
    { field: 'status', headerName: 'Status', renderValue: (v) => v?.replace('_', ' ').toUpperCase() },
    { field: 'sent_date', headerName: 'Sent Date', renderValue: (v) => v ? formatDate(v) : '' },
    { field: 'expected_return_date', headerName: 'Expected Return', renderValue: (v) => v ? formatDate(v) : '' },
    { field: 'actual_return_date', headerName: 'Actual Return', renderValue: (v) => v ? formatDate(v) : '' },
    { field: 'estimated_cost', headerName: 'Est. Cost', renderValue: (v) => v ? `UGX ${Number(v).toLocaleString()}` : '' },
  ], [])

  const fetchRepairs = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filters.status) params.status = filters.status
      if (filters.repair_type) params.repair_type = filters.repair_type

      const response = await api.get('/assets/external-repairs/', { params })
      setRepairs(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching external repairs:', error)
      toast.error('Failed to load external repairs')
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }))
  }

  const clearFilters = () => {
    setFilters({ status: '', repair_type: '' })
  }

  const hasActiveFilters = filters.status || filters.repair_type

  const getActiveFilterLabels = () => {
    const labels = {}
    if (filters.status) labels.Status = filters.status.replace('_', ' ')
    if (filters.repair_type) labels.Type = REPAIR_TYPES.find(t => t.value === filters.repair_type)?.label || filters.repair_type
    return labels
  }

  const fetchOptions = async () => {
    try {
      const [assetsRes, vendorsRes] = await Promise.all([
        api.get('/assets/assets/'),
        api.get('/vendors/'),  // Vendors are at /vendors/ not /vendors/vendors/
      ])
      setAssets(assetsRes.data.results || assetsRes.data)
      setVendors(vendorsRes.data.results || vendorsRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  useEffect(() => {
    fetchRepairs()
  }, [filters])

  useEffect(() => {
    fetchOptions()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async () => {
    if (!formData.asset || !formData.vendor || !formData.problem_description) {
      setErrors({
        asset: !formData.asset ? 'Asset is required' : '',
        vendor: !formData.vendor ? 'Vendor is required' : '',
        problem_description: !formData.problem_description ? 'Problem description is required' : '',
      })
      return
    }

    setFormLoading(true)
    try {
      await api.post('/assets/external-repairs/', {
        ...formData,
        expected_return_date: formData.expected_return_date || null,
        estimated_cost: formData.estimated_cost || null,
      })
      fetchRepairs()
      setFormOpen(false)
      setFormData(initialFormData)
      toast.success('External repair created successfully')
    } catch (error) {
      console.error('Error creating external repair:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
        toast.error('Please fix the errors in the form')
      } else {
        toast.error('Failed to create external repair')
      }
    } finally {
      setFormLoading(false)
    }
  }

  const handleAction = async (action, repairId) => {
    try {
      await api.post(`/assets/external-repairs/${repairId}/${action}/`)
      fetchRepairs()
      setDetailOpen(false)
      toast.success(`Repair ${action.replace('_', ' ')} successful`)
    } catch (error) {
      console.error(`Error performing ${action}:`, error)
      toast.error(`Failed to ${action.replace('_', ' ')}`)
    }
  }

  const handleRowClick = (row) => {
    setSelectedRepair(row)
    setDetailOpen(true)
  }

  const getActionButtons = (repair) => {
    if (!repair) return null
    if (!hasPermission('repairs.manage')) return null

    switch (repair.status) {
      case 'scheduled':
        return (
          <Button
            startIcon={<Send />}
            onClick={() => handleAction('send_out', repair.id)}
          >
            Mark Sent Out
          </Button>
        )
      case 'sent_out':
        return (
          <Button
            startIcon={<Build />}
            onClick={() => handleAction('mark_in_repair', repair.id)}
          >
            In Repair
          </Button>
        )
      case 'in_repair':
        return (
          <Button
            startIcon={<LocalShipping />}
            onClick={() => handleAction('ready_pickup', repair.id)}
          >
            Ready for Pickup
          </Button>
        )
      case 'ready_pickup':
        return (
          <Button
            startIcon={<CheckCircle />}
            color="success"
            onClick={() => handleAction('return_equipment', repair.id)}
          >
            Mark Returned
          </Button>
        )
      default:
        return null
    }
  }

  const getStatusTimeline = (repair) => {
    if (!repair) return null

    const timeline = []
    if (repair.created_at) {
      timeline.push({ label: 'Created', date: repair.created_at })
    }
    if (repair.sent_date) {
      timeline.push({ label: 'Sent Out', date: repair.sent_date })
    }
    if (repair.actual_return_date) {
      timeline.push({ label: 'Returned', date: repair.actual_return_date })
    }

    return timeline
  }

  return (
    <Box>
      <PageHeader
        title="External Repairs"
        subtitle="Track equipment sent for external repair"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets', path: '/assets' },
          { label: 'External Repairs' },
        ]}
        actions={
          <>
            <ExportButtons
              data={repairs}
              columns={exportColumns}
              title="External Repairs"
              filename="external_repairs"
              filters={getActiveFilterLabels()}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              color={hasActiveFilters ? 'primary' : 'inherit'}
            >
              Filters {hasActiveFilters && `(${Object.values(filters).filter(Boolean).length})`}
            </Button>
            <PermissionGate permission="repairs.schedule">
              <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
                Schedule Repair
              </Button>
            </PermissionGate>
          </>
        }
      />

      {/* Filters Section */}
      {showFilters && (
        <Card sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={handleFilterChange('status')}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="sent_out">Sent Out</MenuItem>
                <MenuItem value="in_repair">In Repair</MenuItem>
                <MenuItem value="ready_pickup">Ready for Pickup</MenuItem>
                <MenuItem value="returned">Returned</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Repair Type</InputLabel>
              <Select
                value={filters.repair_type}
                onChange={handleFilterChange('repair_type')}
                label="Repair Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {REPAIR_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                size="small"
              >
                Clear
              </Button>
            )}
          </Stack>
        </Card>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={repairs}
          loading={loading}
          onRowClick={handleRowClick}
          searchPlaceholder="Search repairs..."
          emptyMessage="No external repairs found"
        />
      </Card>

      {/* Create External Repair Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule External Repair</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth error={Boolean(errors.asset)} required>
                <InputLabel>Asset</InputLabel>
                <Select
                  name="asset"
                  value={formData.asset}
                  onChange={handleChange}
                  label="Asset"
                >
                  {assets.map((asset) => (
                    <MenuItem key={asset.id} value={asset.id}>
                      {asset.asset_tag} - {asset.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={Boolean(errors.vendor)} required>
                <InputLabel>Repair Vendor</InputLabel>
                <Select
                  name="vendor"
                  value={formData.vendor}
                  onChange={handleChange}
                  label="Repair Vendor"
                >
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Repair Type</InputLabel>
                <Select
                  name="repair_type"
                  value={formData.repair_type}
                  onChange={handleChange}
                  label="Repair Type"
                >
                  {REPAIR_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expected Return Date"
                name="expected_return_date"
                type="date"
                value={formData.expected_return_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Problem Description"
                name="problem_description"
                value={formData.problem_description}
                onChange={handleChange}
                error={Boolean(errors.problem_description)}
                helperText={errors.problem_description}
                multiline
                rows={3}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Estimated Cost"
                name="estimated_cost"
                type="number"
                value={formData.estimated_cost}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">UGX</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={formLoading}>
            Schedule Repair
          </Button>
        </DialogActions>
      </Dialog>

      {/* Repair Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          External Repair {selectedRepair?.repair_number}
          <Chip
            label={selectedRepair?.status?.replace('_', ' ').toUpperCase()}
            color={STATUS_COLORS[selectedRepair?.status] || 'default'}
            size="small"
            sx={{ ml: 2 }}
          />
        </DialogTitle>
        <DialogContent>
          {selectedRepair && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {selectedRepair.asset_name} ({selectedRepair.asset_tag})
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <strong>Vendor:</strong> {selectedRepair.vendor_name}
              </Grid>
              <Grid item xs={6}>
                <strong>Repair Type:</strong> {selectedRepair.repair_type?.replace('_', ' ')}
              </Grid>

              <Grid item xs={12}>
                <strong>Problem Description:</strong>
                <Typography variant="body2" color="text.secondary">
                  {selectedRepair.problem_description}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <strong>Sent By:</strong> {selectedRepair.sent_by_name || 'N/A'}
              </Grid>
              <Grid item xs={6}>
                <strong>Sent Date:</strong> {selectedRepair.sent_date ? formatDate(selectedRepair.sent_date) : 'Not yet sent'}
              </Grid>

              <Grid item xs={6}>
                <strong>Expected Return:</strong> {selectedRepair.expected_return_date ? formatDate(selectedRepair.expected_return_date) : 'N/A'}
              </Grid>
              <Grid item xs={6}>
                <strong>Actual Return:</strong> {selectedRepair.actual_return_date ? formatDate(selectedRepair.actual_return_date) : 'N/A'}
              </Grid>

              <Grid item xs={6}>
                <strong>Estimated Cost:</strong> {selectedRepair.estimated_cost ? `UGX ${Number(selectedRepair.estimated_cost).toLocaleString()}` : 'N/A'}
              </Grid>
              <Grid item xs={6}>
                <strong>Actual Cost:</strong> {selectedRepair.actual_cost ? `UGX ${Number(selectedRepair.actual_cost).toLocaleString()}` : 'N/A'}
              </Grid>

              {selectedRepair.invoice_number && (
                <Grid item xs={6}>
                  <strong>Invoice #:</strong> {selectedRepair.invoice_number}
                </Grid>
              )}

              {selectedRepair.repair_summary && (
                <Grid item xs={12}>
                  <strong>Repair Summary:</strong>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRepair.repair_summary}
                  </Typography>
                </Grid>
              )}

              {selectedRepair.parts_replaced && (
                <Grid item xs={12}>
                  <strong>Parts Replaced:</strong>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRepair.parts_replaced}
                  </Typography>
                </Grid>
              )}

              {selectedRepair.warranty_on_repair && (
                <Grid item xs={12}>
                  <strong>Warranty on Repair:</strong> {selectedRepair.warranty_on_repair}
                </Grid>
              )}

              {selectedRepair.returned_to_name && (
                <Grid item xs={6}>
                  <strong>Returned To:</strong> {selectedRepair.returned_to_name}
                </Grid>
              )}

              {selectedRepair.notes && (
                <Grid item xs={12}>
                  <strong>Notes:</strong>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRepair.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>
            Close
          </Button>
          {getActionButtons(selectedRepair)}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ExternalRepairsList
